/**
 * Countdown to the final project's deadline. The formatting logic is a PURE
 * function (testable in vitest, no DOM or real clock); the component just
 * refreshes it every second based on the server's time.
 */
import { type Component, createSignal, onCleanup, onMount, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Clock } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";

/** Signature of `useI18n`'s translator (subset used here). */
type TranslateFn = (message: MessageDescriptor) => string;

export interface CountdownParts {
  /** Has the deadline already passed? */
  overdue: boolean;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** Absolute milliseconds remaining (0 if overdue). */
  remainingMs: number;
}

/** Breaks down the time remaining between `nowMs` and `deadlineMs`. Pure. */
export function countdownParts(deadlineMs: number, nowMs: number): CountdownParts {
  const diff = deadlineMs - nowMs;
  if (diff <= 0) {
    return { overdue: true, days: 0, hours: 0, minutes: 0, seconds: 0, remainingMs: 0 };
  }
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { overdue: false, days, hours, minutes, seconds, remainingMs: diff };
}

/** Compact, localized countdown text. Pure (receives `t`). */
export function formatCountdown(parts: CountdownParts, t: TranslateFn): string {
  if (parts.overdue) {
    return t(msg`Deadline passed`);
  }
  const { days, hours, minutes, seconds } = parts;
  if (days > 0) {
    return t(msg`${days}d ${hours}h left`);
  }
  if (hours > 0) {
    return t(msg`${hours}h ${minutes}m left`);
  }
  return t(msg`${minutes}m ${seconds}s left`);
}

export const Countdown: Component<{ deadlineISO: string; serverNowISO: string }> = (props) => {
  const { t } = useI18n();
  const [nowMs, setNowMs] = createSignal(Date.now());
  // Local clock anchored to the server→client offset on mount (avoids
  // depending on the device clock: base = server time + local elapsed time).
  // The anchoring is done in onMount to read `serverNowISO` only once,
  // deliberately (it's a snapshot at mount time; later prop changes are
  // ignored on purpose because the clock is already anchored).
  onMount(() => {
    const mountedAt = Date.now();
    const serverBase = new Date(props.serverNowISO).getTime();
    setNowMs(serverBase);
    const timer = setInterval(() => setNowMs(serverBase + (Date.now() - mountedAt)), 1000);
    onCleanup(() => clearInterval(timer));
  });

  const parts = () => countdownParts(new Date(props.deadlineISO).getTime(), nowMs());

  return (
    <span
      class={`inline-flex items-center gap-1 text-sm font-semibold ${
        parts().overdue ? "text-danger" : "text-accent"
      }`}
      data-testid="final-project-countdown"
      data-overdue={parts().overdue ? "true" : "false"}
    >
      <Clock class="h-3.5 w-3.5" />
      <Show when={!parts().overdue} fallback={t(msg`Deadline passed`)}>
        {formatCountdown(parts(), t)}
      </Show>
    </span>
  );
};
